import React, { useState, useEffect } from 'react';
import { FaArrowRight, FaCheck, FaRegClock, FaKey } from 'react-icons/fa';
import Modal from './Modal';
import axiosInstance from '../utility/axiosInstance';
import { useToast } from './ToastContext';
import { HA_MEMBERS } from './HaOption';
// Re-exported so the two pages keep importing one thing; the rule itself lives in a
// dependency-free module so it can be tested. See its header.
export { haUpgradeOption } from './haUpgradeOption';
export type { HaUpgradeRow } from './haUpgradeOption';
import '../styles/DatabasesPage.scss';

/**
 * Turn a LIVE single-machine managed database into a highly available one, in place.
 *
 * ⛔ THE ONE THING THIS SCREEN EXISTS TO SAY: writes made to the current machine during the copy
 *    are NOT carried over and cannot be recovered. That is measured, not a disclaimer - on a live
 *    upgrade a row written after the copy started was absent from the cluster afterwards (5001 on
 *    the source, 5000 on the cluster). So the acknowledgement is a checkbox the user has to tick,
 *    not a line of small print under a button, and the button stays disabled until they do.
 *
 * The other thing people get wrong: nothing repoints their application. They get a new address and
 * must redeploy against it. A user who reads "now highly available" and stops has an app still
 * pointed at the single machine - which keeps working, so nothing surfaces it until that machine
 * dies, i.e. the exact event they just paid to survive. It is the last thing on the screen.
 *
 * Short lines, not paragraphs (Milos, 2026-10-27: "a wall of text ... kills that premium look"; the
 * create form's HA option follows the same rule). Every fact above is still on the screen; the
 * four steps of how the upgrade runs are one click away under "How it works", not deleted.
 */

// A month is ~730 hours, matching HaOption. The rate comes from the selected size's REAL
// hourly_rate; a hardcoded price table here is a number the customer argues about later.
const HOURS_PER_MONTH = 730;

export interface UpgradeToHaTarget {
    id: number;
    name: string;
    /** The engine's own words, for the copy ("PostgreSQL", "ClickHouse"). */
    serviceLabel: string;
    /** '/v1/databases' or '/v1/clickhouse'. */
    apiBase: string;
    /** Its current machine size, so the cost can be real rather than "about 3x". */
    hourlyRate?: number | null;
}

interface UpgradeToHaModalProps {
    isOpen: boolean;
    onClose: () => void;
    target: UpgradeToHaTarget | null;
    /** Called after the upgrade has been accepted, so the page can refetch and show it running. */
    onStarted: () => void;
}

const UpgradeToHaModal: React.FC<UpgradeToHaModalProps> = ({ isOpen, onClose, target, onStarted }) => {
    const { showToast } = useToast();
    const [acknowledged, setAcknowledged] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Never carry a previous target's tick into a new one. An acknowledgement is about THIS
    // database's data, and a sticky checkbox would let the second upgrade skip the decision.
    useEffect(() => { setAcknowledged(false); }, [isOpen, target?.id]);

    if (!target) return null;

    const rate = target.hourlyRate != null && Number.isFinite(Number(target.hourlyRate))
        ? Number(target.hourlyRate) : null;
    const haHour = rate != null ? rate * HA_MEMBERS : null;
    const singleMonth = rate != null ? rate * HOURS_PER_MONTH : null;
    const haMonth = haHour != null ? haHour * HOURS_PER_MONTH : null;

    const handleSubmit = async () => {
        if (!acknowledged) return;
        setSubmitting(true);
        try {
            await axiosInstance.post(`${target.apiBase}/${target.id}/upgrade-to-ha`, {
                acknowledge_write_loss: true,
            });
            showToast(`Upgrading ${target.name} to high availability. It takes around 20 minutes.`, 'success');
            onStarted();
            onClose();
        } catch (err: any) {
            // The API's own message, not a generic one: its refusals are specific and actionable
            // ("already highly available", "still provisioning", "an upgrade is already running").
            const e = err?.response?.data?.error;
            showToast(e?.message || 'Could not start the upgrade', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`Make ${target.name} highly available`}>
            <div className="ha-upgrade-modal">
                {/* What it does, as a picture: one machine becomes three behind a load balancer. */}
                <div className="ha-upgrade-diagram" aria-hidden="true">
                    <div className="ha-upgrade-node">1 machine</div>
                    <FaArrowRight className="ha-upgrade-arrow" />
                    <div className="ha-upgrade-cluster">
                        <div className="ha-upgrade-lb">Load balancer</div>
                        <div className="ha-upgrade-members">
                            {Array.from({ length: HA_MEMBERS }, (_, i) => <span key={i} className="ha-upgrade-member" />)}
                        </div>
                    </div>
                </div>
                <p className="ha-upgrade-lead">
                    {HA_MEMBERS} machines behind a load balancer. If one stops, another takes over.
                </p>

                <ul className="ha-upgrade-facts">
                    <li><FaCheck /> Stays online</li>
                    <li><FaRegClock /> About 20 minutes</li>
                    <li><FaKey /> Same data and login</li>
                </ul>

                <details className="ha-upgrade-how">
                    <summary>How it works</summary>
                    <ol>
                        <li>A full backup of the current machine, as a safety copy.</li>
                        <li>{HA_MEMBERS} machines are built alongside it, one at a time.</li>
                        <li>Your data is copied across and checked on both sides.</li>
                        <li>Only then does the address move to the load balancer. If anything fails before that, nothing changes.</li>
                    </ol>
                </details>

                {/* The cost, always visible, never behind a hover - same rule as the create form. */}
                <div className="ha-upgrade-cost">
                    {rate == null ? (
                        <div className="ha-upgrade-price">
                            <strong>{HA_MEMBERS}x</strong> <span>the hourly rate of its current size, billed as {HA_MEMBERS} machines</span>
                        </div>
                    ) : (
                        <div className="ha-upgrade-price">
                            <strong>£{haMonth!.toFixed(2)}</strong> <span>/ month, billed as {HA_MEMBERS} machines</span>
                            <em>instead of £{singleMonth!.toFixed(2)} today</em>
                        </div>
                    )}
                    <div className="ha-upgrade-cost-note">
                        The old machine stays up 3 days for the switch-over, billed until it is removed.
                    </div>
                </div>

                {/* ⛔ The measured one. Short, but a decision: the button below is dead until it is ticked. */}
                <label className="ha-upgrade-ack">
                    <input
                        type="checkbox"
                        checked={acknowledged}
                        onChange={e => setAcknowledged(e.target.checked)}
                    />
                    <span>
                        Writes made <strong>during the copy</strong> are not carried over. I will pause live writes first.
                    </span>
                </label>

                <p className="ha-upgrade-after">
                    <strong>Afterwards, point your app at the new address</strong> within 3 days.
                </p>

                <div className="modal-actions">
                    <button className="cancel-button" onClick={onClose} disabled={submitting}>Cancel</button>
                    <button
                        className="submit-button"
                        onClick={handleSubmit}
                        disabled={!acknowledged || submitting}
                        title={acknowledged ? undefined : 'Confirm you understand the write-loss window first'}
                    >
                        {submitting ? 'Starting…' : `Upgrade to ${HA_MEMBERS} machines`}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default UpgradeToHaModal;
